import { useCallback, useEffect, useMemo, useState } from 'react'
import { Controller, useForm, useWatch, type FieldError, type UseFormRegister } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { CKEditor4 } from '@/components/editor/CKEditor4'
import { DatePicker } from '@/components/ui/DatePicker'
import {
  primaryEntityActionButtonClass,
  secondaryEntityActionButtonClass,
} from '@/components/ui/entityActionButtonClasses'
import { OnScreenModal } from '@/components/ui/OnScreenModal'
import { cn } from '@/lib/utils/helpers'
import { showApiError } from '@/lib/utils/notifications'
import { getCompaniesForSchedule, uploadEmailContentImage } from '../services/reportsSchedulerService'
import type {
  CompanyRecipient,
  ReportScheduleModalProps,
} from '../types'
import {
  MONTHLY_SEND_DAYS,
  REPORT_DATE_RANGES,
  REPORT_FREQUENCIES,
  REPORT_STATUSES,
  WEEKLY_SEND_DAYS,
  reportScheduleCriteriaStepSchema,
  reportScheduleFormSchema,
  type ReportScheduleFormValues,
} from '../types/schemas'
import {
  appendUploadedImageHtml,
  mapFormValuesToPayload,
  mapScheduleToFormValues,
} from '../utils/reportsSchedulerFormatters'
import { SCHEDULE_START_DATE_PICKER_PROPS } from '../constants'

const INPUT_CLASS =
  'w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-[#1597ff] focus:outline-none focus:ring-2 focus:ring-[#1597ff]/25'
const LABEL_BASE_CLASS = 'mb-1 block text-left text-sm font-medium'
const INPUT_ERROR_CLASS = 'border-red-500 focus:border-red-500 focus:ring-red-500/20'

function ErrorText({ error }: { error?: { message?: string } }) {
  if (!error?.message) return null
  return (
    <p className="mt-1 text-xs !text-red-600" role="alert">
      {error.message}
    </p>
  )
}

function TextInput({
  id,
  label,
  testId,
  register,
  name,
  error,
  type = 'text',
  required = false,
  max,
}: {
  id: string
  label: string
  testId?: string
  register: UseFormRegister<ReportScheduleFormValues>
  name: keyof ReportScheduleFormValues
  error?: FieldError
  type?: string
  required?: boolean
  max?: string
}) {
  const labelClass = `${LABEL_BASE_CLASS} text-slate-700`
  const inputClass = error ? `${INPUT_CLASS} ${INPUT_ERROR_CLASS}` : INPUT_CLASS

  return (
    <div>
      <label htmlFor={id} className={labelClass}>
        {label}
        {required ? <span className="text-red-600"> *</span> : null}
      </label>
      <input id={id} type={type} max={max} className={inputClass} data-testid={testId} {...register(name)} />
      <ErrorText error={error} />
    </div>
  )
}

export function ReportScheduleModal({
  isOpen,
  schedule,
  reportCatalog,
  submitting,
  onClose,
  onSubmit,
}: ReportScheduleModalProps) {
  const [step, setStep] = useState<'criteria' | 'template'>('criteria')
  const [companies, setCompanies] = useState<CompanyRecipient[]>([])
  const [companiesLoading, setCompaniesLoading] = useState(false)
  const [uploadingImage, setUploadingImage] = useState(false)

  const {
    register,
    control,
    handleSubmit,
    reset,
    setValue,
    trigger,
    getValues,
    formState: { errors, isValid },
  } = useForm<ReportScheduleFormValues>({
    resolver: zodResolver(reportScheduleFormSchema),
    mode: 'onChange',
    defaultValues: mapScheduleToFormValues(null),
  })

  const formValues = useWatch({ control })
  const frequency = useWatch({ control, name: 'frequency' })
  const listed = useWatch({ control, name: 'listed' })
  const watchedCompanies = useWatch({ control, name: 'fk_company_id' })
  const selectedCompanies = useMemo(() => watchedCompanies ?? [], [watchedCompanies])
  const selectedReport = useWatch({ control, name: 'report_name' })
  const emailBody = useWatch({ control, name: 'email_body' }) ?? ''

  useEffect(() => {
    if (!isOpen) return
    setStep('criteria')
    reset(mapScheduleToFormValues(schedule))
  }, [isOpen, reset, schedule])

  useEffect(() => {
    if (!isOpen) return
    let cancelled = false
    setCompaniesLoading(true)
    void getCompaniesForSchedule(listed)
      .then((data) => {
        if (!cancelled) setCompanies(data)
      })
      .catch((error) => {
        if (!cancelled) showApiError(error)
      })
      .finally(() => {
        if (!cancelled) setCompaniesLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [isOpen, listed])

  useEffect(() => {
    if (frequency === 'DAILY' && getValues('send_day')) {
      setValue('send_day', '', { shouldDirty: true, shouldValidate: true })
    }
  }, [frequency, getValues, setValue])

  const sendDayOptions = useMemo(() => {
    if (frequency === 'WEEKLY') return WEEKLY_SEND_DAYS
    if (frequency === 'MONTHLY') return MONTHLY_SEND_DAYS
    return []
  }, [frequency])

  const variableGroups = useMemo(() => {
    if (selectedReport && reportCatalog[selectedReport]) {
      return [[selectedReport, reportCatalog[selectedReport]]] as const
    }
    return Object.entries(reportCatalog)
  }, [reportCatalog, selectedReport])
  const availableVariables = useMemo(
    () =>
      variableGroups.flatMap(([reportKey, report]) =>
        Object.entries(report.available_variables ?? {}).map(([variable, label]) => ({
          key: `${reportKey}-${variable}`,
          variable,
          label,
        })),
      ),
    [variableGroups],
  )

  const canProceedToEmailStep = useMemo(
    () => reportScheduleCriteriaStepSchema.safeParse(formValues ?? {}).success,
    [formValues],
  )

  const selectedCompanySet = useMemo(
    () => new Set(selectedCompanies.map(Number)),
    [selectedCompanies],
  )
  const visibleCompanyIds = useMemo(
    () => companies.map((company) => Number(company.company_id)).filter(Number.isFinite),
    [companies],
  )
  /**
   * Same as Angular `create-schedule.component.html` select-all checkbox:
   * `[checked]="selectedPMCList?.length === companyList?.length"`
   */
  const selectAllHeaderChecked =
    companies.length > 0 && selectedCompanies.length === companies.length

  const setSelectedCompanyIds = useCallback(
    (ids: number[]) => {
      setValue('fk_company_id', ids, { shouldDirty: true, shouldTouch: true, shouldValidate: true })
    },
    [setValue],
  )

  const toggleCompany = (companyId: number, checked: boolean) => {
    const next = new Set(selectedCompanySet)
    if (checked) next.add(companyId)
    else next.delete(companyId)
    setSelectedCompanyIds(Array.from(next))
  }

  /**
   * Matches Angular `selectAllPMC`: checking replaces selection with the current table rows only;
   * unchecking clears the entire selection (does not preserve off-screen picks).
   */
  const toggleAllVisible = (checked: boolean) => {
    if (checked) {
      setSelectedCompanyIds(visibleCompanyIds.slice())
    } else {
      setSelectedCompanyIds([])
    }
  }

  const goNext = async () => {
    const ok = await trigger([
      'friendly_name',
      'report_name',
      'date_range',
      'report_status',
      'frequency',
      'send_day',
      'send_time',
      'start_date',
      'fk_company_id',
    ])
    if (ok) setStep('template')
  }

  const handlePaste = async (event: React.ClipboardEvent<HTMLDivElement>) => {
    const imageFile = Array.from(event.clipboardData.files).find((file) =>
      file.type.startsWith('image/'),
    )
    if (!imageFile) return

    event.preventDefault()
    setUploadingImage(true)
    try {
      const imageUrl = await uploadEmailContentImage(imageFile)
      setValue('email_body', appendUploadedImageHtml(emailBody, imageUrl), {
        shouldDirty: true,
        shouldValidate: true,
      })
    } catch (error) {
      showApiError(error)
    } finally {
      setUploadingImage(false)
    }
  }

  const internalSubmit = (values: ReportScheduleFormValues) => {
    onSubmit(mapFormValuesToPayload(values), schedule?.rs_id)
  }

  const formId = 'reports-scheduler-form'

  return (
    <OnScreenModal
      isOpen={isOpen}
      title={schedule ? 'Edit Schedule' : 'Create Schedule'}
      onClose={onClose}
      maxWidth="max-w-800"
      bodyScrollOnly
      panelDataTestId="reports-scheduler-modal-panel"
      titleDataTestId="reports-scheduler-modal-title"
      closeButtonDataTestId="reports-scheduler-modal-close"
      headerClassName="text-center"
      footerAlign="center"
      footer={
        <div
          className="flex flex-wrap items-center justify-center gap-3"
          data-testid="reports-scheduler-modal-footer"
        >
          {step === 'criteria' ? (
            <button
              type="button"
              disabled={!canProceedToEmailStep}
              title={
                !canProceedToEmailStep
                  ? 'Complete criteria & schedule, including at least one PMC recipient'
                  : undefined
              }
              onClick={() => void goNext()}
              className={cn(primaryEntityActionButtonClass, 'min-w-[120px]')}
              data-testid="reports-scheduler-next-button"
            >
              Next
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setStep('criteria')}
                className={cn(secondaryEntityActionButtonClass, 'min-w-[120px]')}
                data-testid="reports-scheduler-back-button"
              >
                Back
              </button>
              <button
                type="submit"
                form={formId}
                disabled={submitting || !isValid}
                className={cn(primaryEntityActionButtonClass, 'min-w-[120px]')}
                data-testid="reports-scheduler-save-button"
              >
                {submitting ? 'Saving...' : 'Save'}
              </button>
            </>
          )}
          <button
            type="button"
            onClick={onClose}
            className={cn(secondaryEntityActionButtonClass, 'min-w-[120px]')}
            data-testid="reports-scheduler-cancel-button"
          >
            Cancel
          </button>
        </div>
      }
    >
      <form
        id={formId}
        onSubmit={handleSubmit(internalSubmit)}
        className="min-w-0 space-y-4 p-2"
        data-testid="reports-scheduler-form"
        noValidate
      >
        <div className="flex items-center gap-1 rounded-full bg-slate-100 p-1 text-xs font-medium text-slate-600 sm:gap-2 sm:text-sm">
          <button
            type="button"
            onClick={() => setStep('criteria')}
            className={`min-w-0 flex-1 rounded-full px-2 py-2 text-center transition-colors sm:px-3 ${
              step === 'criteria' ? 'bg-white text-[#1597ff] shadow-sm' : 'hover:text-slate-900'
            }`}
            data-testid="reports-scheduler-step-criteria"
          >
            Criteria & Schedule
          </button>
          <button
            type="button"
            disabled={step === 'criteria' && !canProceedToEmailStep}
            title={
              step === 'criteria' && !canProceedToEmailStep
                ? 'Complete criteria & schedule, including at least one PMC recipient'
                : undefined
            }
            onClick={() => void goNext()}
            className={`min-w-0 flex-1 rounded-full px-2 py-2 text-center transition-colors disabled:cursor-not-allowed disabled:opacity-50 sm:px-3 ${
              step === 'template' ? 'bg-white text-[#1597ff] shadow-sm' : 'hover:text-slate-900'
            }`}
            data-testid="reports-scheduler-step-template"
          >
            Email Template
          </button>
        </div>

        {step === 'criteria' ? (
          <div className="min-w-0 space-y-5">
            <section className="rounded-xl border border-slate-200 bg-white p-3 sm:p-4">
              <h4 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500">
                Criteria & Schedule
              </h4>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="min-w-0">
                  <TextInput
                    id="schedule-friendly-name"
                    label="Friendly Name"
                    testId="reports-scheduler-friendly-name"
                    register={register}
                    name="friendly_name"
                    error={errors.friendly_name}
                    required
                  />
                </div>
                <div className="min-w-0">
                  <label
                    htmlFor="schedule-report"
                    className={`${LABEL_BASE_CLASS} text-left text-slate-700`}
                  >
                    Report <span className="text-red-600">*</span>
                  </label>
                  <select
                    id="schedule-report"
                    className={errors.report_name ? `${INPUT_CLASS} ${INPUT_ERROR_CLASS}` : INPUT_CLASS}
                    data-testid="reports-scheduler-report-select"
                    {...register('report_name')}
                  >
                    <option value="">Select Report</option>
                    {Object.entries(reportCatalog).map(([key, report]) => (
                      <option key={key} value={key}>
                        {report.name}
                      </option>
                    ))}
                  </select>
                  <ErrorText error={errors.report_name} />
                </div>
                <div className="min-w-0">
                  <label
                    htmlFor="schedule-date-range"
                    className={`${LABEL_BASE_CLASS} text-left text-slate-700`}
                  >
                    Date Range <span className="text-red-600">*</span>
                  </label>
                  <select
                    id="schedule-date-range"
                    className={errors.date_range ? `${INPUT_CLASS} ${INPUT_ERROR_CLASS}` : INPUT_CLASS}
                    data-testid="reports-scheduler-date-range-select"
                    {...register('date_range')}
                  >
                    <option value="">Select Date Range</option>
                    {REPORT_DATE_RANGES.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <ErrorText error={errors.date_range} />
                </div>
                <div className="min-w-0">
                  <label
                    htmlFor="schedule-status"
                    className={`${LABEL_BASE_CLASS} text-left text-slate-700`}
                  >
                    Status <span className="text-red-600">*</span>
                  </label>
                  <select
                    id="schedule-status"
                    className={errors.report_status ? `${INPUT_CLASS} ${INPUT_ERROR_CLASS}` : INPUT_CLASS}
                    data-testid="reports-scheduler-status-select"
                    {...register('report_status')}
                  >
                    {REPORT_STATUSES.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <ErrorText error={errors.report_status} />
                </div>
              </div>

              <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <div className="min-w-0">
                  <label
                    htmlFor="schedule-frequency"
                    className={`${LABEL_BASE_CLASS} text-slate-700`}
                  >
                    Frequency <span className="text-red-600">*</span>
                  </label>
                  <select
                    id="schedule-frequency"
                    className={errors.frequency ? `${INPUT_CLASS} ${INPUT_ERROR_CLASS}` : INPUT_CLASS}
                    data-testid="reports-scheduler-frequency-select"
                    {...register('frequency')}
                  >
                    {REPORT_FREQUENCIES.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <ErrorText error={errors.frequency} />
                </div>
                {frequency !== 'DAILY' ? (
                  <div className="min-w-0">
                    <label
                      htmlFor="schedule-send-day"
                      className={`${LABEL_BASE_CLASS} text-slate-700`}
                    >
                      On 
                    </label>
                    <select
                      id="schedule-send-day"
                      className={errors.send_day ? `${INPUT_CLASS} ${INPUT_ERROR_CLASS}` : INPUT_CLASS}
                      data-testid="reports-scheduler-send-day-select"
                      {...register('send_day')}
                    >
                      <option value="">Select Date</option>
                      {sendDayOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <ErrorText error={errors.send_day} />
                  </div>
                ) : null}
                <TextInput
                  id="schedule-time"
                  label="At"
                  testId="reports-scheduler-time-input"
                  register={register}
                  name="send_time"
                  type="time"
                  error={errors.send_time}
                  required
                />
                <div className="min-w-0">
                  <label
                    htmlFor="schedule-start-date"
                    className={`${LABEL_BASE_CLASS} text-slate-700`}
                  >
                    Start From 
                  </label>
                  <Controller
                    name="start_date"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        id="schedule-start-date"
                        name={field.name}
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        error={errors.start_date?.message}
                        className={errors.start_date ? `${INPUT_CLASS} ${INPUT_ERROR_CLASS}` : INPUT_CLASS}
                        dataTestId="report-schedule-start-date"
                        {...SCHEDULE_START_DATE_PICKER_PROPS}
                      />
                    )}
                  />
                </div>
              </div>
            </section>

            <section className="min-w-0 rounded-xl border border-slate-200 bg-white p-3 sm:p-4">
              <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
                <div
                  className={`rounded-lg border px-3 py-3 sm:px-4 ${
                    errors.fk_company_id
                      ? 'border-red-300 bg-red-50'
                      : 'border-[#1597ff]/25 bg-[#1597ff]/10'
                  }`}
                >
                  <h4
                    className={`text-sm font-bold uppercase tracking-wide ${
                      errors.fk_company_id ? 'text-red-600' : 'text-[#0b5cb3]'
                    }`}
                  >
                    PMC Recipients <span className="text-red-600 normal-case">*</span>
                  </h4>
                  <p className="text-sm font-medium text-slate-700">
                    {selectedCompanies.length} selected
                  </p>
                  <ErrorText error={errors.fk_company_id} />
                </div>
                <div className="flex w-full shrink-0 rounded-full bg-slate-100 p-1 text-sm sm:w-auto">
                  {[
                    { value: '', label: 'All' },
                    { value: '1', label: 'Active' },
                    { value: '0', label: 'Inactive' },
                  ].map((option) => (
                    <label
                      key={option.label}
                      className={`flex-1 cursor-pointer rounded-full px-3 py-1.5 text-center sm:flex-none ${
                        listed === option.value ? 'bg-white text-[#1597ff] shadow-sm' : 'text-slate-600'
                      }`}
                    >
                      <input
                        type="radio"
                        value={option.value}
                        className="sr-only"
                        {...register('listed')}
                      />
                      {option.label}
                    </label>
                  ))}
                </div>
              </div>

              <div className="max-h-[360px] overflow-x-auto overflow-y-auto rounded-lg border border-slate-200">
                <table className="w-full min-w-[560px] text-sm">
                  <thead className="sticky top-0 bg-slate-50 text-slate-700">
                    <tr>
                      <th className="whitespace-nowrap px-3 py-2 text-center">
                        <label className="inline-flex items-center justify-center gap-2">
                          <input
                            type="checkbox"
                            checked={selectAllHeaderChecked}
                            onChange={(event) => toggleAllVisible(event.target.checked)}
                            data-testid="reports-scheduler-select-all-pmc"
                          />
                          Select All
                        </label>
                      </th>
                      <th className="whitespace-nowrap px-3 py-2 text-left">PMC</th>
                      <th className="whitespace-nowrap px-3 py-2 text-center">Properties</th>
                      <th className="whitespace-nowrap px-3 py-2 text-center">Residents</th>
                      <th className="whitespace-nowrap px-3 py-2 text-center">Contracts YTD</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {companiesLoading ? (
                      <tr>
                        <td colSpan={5} className="px-3 py-8 text-center text-slate-500">
                          Loading PMCs...
                        </td>
                      </tr>
                    ) : companies.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-3 py-8 text-center text-slate-500">
                          No PMCs found.
                        </td>
                      </tr>
                    ) : (
                      companies.map((company) => (
                        <tr key={company.company_id}>
                          <td className="px-3 py-2 text-center">
                            <input
                              type="checkbox"
                              checked={selectedCompanySet.has(Number(company.company_id))}
                              onChange={(event) =>
                                toggleCompany(Number(company.company_id), event.target.checked)
                              }
                              aria-label={`Select ${company.company_name}`}
                              data-testid={`reports-scheduler-pmc-${company.company_id}`}
                            />
                          </td>
                          <td className="break-words px-3 py-2 text-left font-medium text-slate-900">
                            {company.company_name}
                          </td>
                          <td className="whitespace-nowrap px-3 py-2 text-center text-slate-700">
                            {company.property_count ?? '-'}
                          </td>
                          <td className="whitespace-nowrap px-3 py-2 text-center text-slate-700">
                            {company.resident_count ?? '-'}
                          </td>
                          <td className="whitespace-nowrap px-3 py-2 text-center text-slate-700">
                            {company.contract ?? '-'}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        ) : (
          <section className="min-w-0 space-y-5 rounded-xl border border-slate-200 bg-white p-3 sm:p-4">
            <div className="grid min-w-0 gap-4 lg:grid-cols-[minmax(0,1fr)_280px]">
              <div className="grid min-w-0 gap-4 sm:grid-cols-2">
                <TextInput
                  id="schedule-email-from"
                  label="From"
                  testId="reports-scheduler-email-from"
                  register={register}
                  name="email_from"
                  error={errors.email_from}
                  required
                />
                <TextInput
                  id="schedule-email-to"
                  label="To (email1, email2)"
                  testId="reports-scheduler-email-to"
                  register={register}
                  name="email_to"
                  error={errors.email_to}
                  required
                />
                <TextInput
                  id="schedule-email-cc"
                  label="CC"
                  testId="reports-scheduler-email-cc"
                  register={register}
                  name="email_cc"
                  error={errors.email_cc}
                />
                <TextInput
                  id="schedule-email-bcc"
                  label="BCC"
                  testId="reports-scheduler-email-bcc"
                  register={register}
                  name="email_bcc"
                  error={errors.email_bcc}
                />
                <TextInput
                  id="schedule-reply-to"
                  label="Reply To"
                  testId="reports-scheduler-reply-to"
                  register={register}
                  name="reply_to"
                  error={errors.reply_to}
                />
                <TextInput
                  id="schedule-email-subject"
                  label="Subject"
                  testId="reports-scheduler-email-subject"
                  register={register}
                  name="email_subject"
                  error={errors.email_subject}
                  required
                />
              </div>

              <aside className="min-w-0 rounded-lg bg-sky-50 p-3 text-sm text-slate-700">
                <h4 className="mb-2 font-semibold text-slate-900">Available Variables</h4>
                <div className="max-h-52 space-y-2 overflow-y-auto">
                  {availableVariables.length === 0 ? (
                    <p className="text-slate-500">Select a report to see variables.</p>
                  ) : (
                    availableVariables.map((variableItem) => (
                      <p key={variableItem.key} className="break-words">
                        <span className="font-semibold text-[#1597ff]">${variableItem.variable}</span> -{' '}
                        {variableItem.label}
                      </p>
                    ))
                  )}
                </div>
              </aside>
            </div>

            <div className="min-w-0" onPaste={handlePaste}>
              <label className={`${LABEL_BASE_CLASS} text-slate-700`}>
                Email Body <span className="text-red-600">*</span>
              </label>
              <Controller
                control={control}
                name="email_body"
                render={({ field }) => (
                  <CKEditor4
                    id="schedule-email-body"
                    value={field.value}
                    onChange={field.onChange}
                    className="min-h-[220px] min-w-0 [&_.ck-editor__editable]:min-h-[180px] sm:min-h-[260px] sm:[&_.ck-editor__editable]:min-h-[220px]"
                  />
                )}
              />
              {uploadingImage ? (
                <p className="mt-1 text-xs text-slate-500">Uploading pasted image...</p>
              ) : null}
              <ErrorText error={errors.email_body} />
            </div>
          </section>
        )}
      </form>
    </OnScreenModal>
  )
}
